import React, { useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@shared/ui/dialog";
import { Stepper } from "@shared/components/Stepper";
import { FileUp } from "lucide-react";
import { ImportSummary } from "./types";
import { ConfigureStep } from "./stepComponents/ConfigureStep";
import { PreviewValidateStep } from "./stepComponents/PreviewValidateStep";
import { DoneStep } from "./stepComponents/DoneStep";
import { useToast } from "@shared/hooks/use-toast";
import { onboardingApi } from "@features/onboarding/utility-setup/api";
import {
  useRemoteUtilityId,
  useTenantId,
} from "@shared/selectors/globalSelectors";
import { useEntityTypes } from "@features/onboarding/utility-setup/hooks";

interface ImportV2ModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultEntityType?: string;
}

// Helper to map tab/configLevel string or key to numeric string entity key
export const findKeyForEntityType = (entityTypes: any[], defaultType?: string): string => {
  if (!entityTypes || entityTypes.length === 0) return "2";
  if (!defaultType) return entityTypes[0].key.toString();

  // Check if defaultType is already a numeric key string (e.g. "2", "10")
  const numericKey = Number(defaultType);
  if (!isNaN(numericKey)) {
    const found = entityTypes.find((e) => Number(e.key) === numericKey);
    if (found) return found.key.toString();
  }

  // Match by value or config level string (e.g. "region", "premise", "sub_area", "unit")
  const normalizedDefault = defaultType.toLowerCase().replace(/[\s_-]/g, "");
  const matched = entityTypes.find((e) => {
    const normValue = String(e.value || "").toLowerCase().replace(/[\s_-]/g, "");
    return (
      normValue === normalizedDefault ||
      normValue.includes(normalizedDefault) ||
      normalizedDefault.includes(normValue)
    );
  });

  return matched ? matched.key.toString() : entityTypes[0].key.toString();
};

const checkRowValidity = (row: any): boolean => {
  if (!row) return false;
  const statusStr = String(row.status || "").toLowerCase();
  if (statusStr === "valid") return true;
  if (
    statusStr === "invalid" ||
    statusStr === "error" ||
    statusStr === "failed"
  )
    return false;

  const msgStr = String(row.message || "").toLowerCase();
  if (
    msgStr === "no issue" ||
    msgStr === "valid" ||
    msgStr === "ok" ||
    msgStr === "success"
  )
    return true;

  if (Array.isArray(row.errors) && row.errors.length > 0) return false;
  if (
    msgStr.includes("error") ||
    msgStr.includes("invalid") ||
    msgStr.includes("fail") ||
    msgStr.includes("missing")
  )
    return false;

  return true;
};

export const ImportV2Modal: React.FC<ImportV2ModalProps> = ({
  isOpen,
  onClose,
  defaultEntityType = "Region",
}) => {
  const { toast } = useToast();
  const tenantID = useTenantId();
  const remoteUtilityId = useRemoteUtilityId();

  const [selectedEntity, setSelectedEntity] = useState<string>("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [dataUploadId, setDataUploadId] = useState<number | null>(null);
  const [validationData, setValidationData] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const { data: entityTypesData } = useEntityTypes();

  const entityTypeOptions = useMemo(() => {
    return (
      entityTypesData?.result?.map((type: any) => ({
        label: type.value,
        value: type.key.toString(),
        id: type.key,
      })) || []
    );
  }, [entityTypesData]);

  // Sync selectedEntity when modal opens or initialEntityType changes
  useEffect(() => {
    if (isOpen && entityTypeOptions.length > 0) {
      const matched = entityTypeOptions.find(
        (e: any) =>
          e.label.toLowerCase() === defaultEntityType.toLowerCase() ||
          e.value === defaultEntityType,
      );
      if (matched) {
        setSelectedEntity(matched.value);
      } else if (!selectedEntity) {
        setSelectedEntity(entityTypeOptions[0].value);
      }
    }
  }, [isOpen, defaultEntityType, entityTypeOptions, selectedEntity]);

  const selectedEntityOption = useMemo(() => {
    return entityTypeOptions.find((e: any) => e.value === selectedEntity);
  }, [entityTypeOptions, selectedEntity]);

  const selectedEntityLabel = selectedEntityOption?.label || "Entity";

  // Compute summary handling error/valid status strings from validateData response
  const summary: ImportSummary = useMemo(() => {
    const totalRows = validationData.length;
    const validRows = validationData.filter((r) => checkRowValidity(r)).length;
    const invalidRows = totalRows - validRows;

    return {
      totalRows,
      validRows,
      invalidRows,
      importedRows: validRows,
      skippedRows: invalidRows,
    };
  }, [validationData]);

  const handleFileValidated = (
    file: File,
    validationResponse: { dataUploadId: number; result: any[] },
    onNext?: () => void
  ) => {
    setUploadedFile(file);
    setDataUploadId(validationResponse.dataUploadId);
    setValidationData(validationResponse.result || []);
    if (onNext) onNext();
  };

  const handleRemoveFile = (onPrevious?: () => void) => {
    setUploadedFile(null);
    setDataUploadId(null);
    setValidationData([]);
    if (onPrevious) onPrevious();
  };

  // Execute upload API
  const handleExecuteImport = async (onNext?: () => void) => {
    if (!dataUploadId || !remoteUtilityId) {
      toast({
        title: "Error",
        description: "Upload ID or Utility ID missing.",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    try {
      await onboardingApi.uploadData(dataUploadId, remoteUtilityId, tenantID);

      toast({
        title: "Import Successful",
        description: `Successfully imported ${summary.validRows} ${selectedEntityLabel} record(s).`,
      });

      if (onNext) onNext();
    } catch (error: any) {
      console.error("Upload error:", error);
      toast({
        title: "Upload Failed",
        description: "Failed to upload data to server. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const steps = useMemo(
    () => [
      {
        title: "Configure",
        slug: "configure",
        component: (
          <ConfigureStep
            selectedEntity={selectedEntity}
            setSelectedEntity={setSelectedEntity}
            onFileValidated={handleFileValidated}
            onCancel={onClose}
          />
        ),
        optional: false,
      },
      {
        title: "Preview & Validate",
        slug: "preview",
        component: (
          <PreviewValidateStep
            entityType={selectedEntityLabel as any}
            fileName={uploadedFile?.name || `${selectedEntityLabel}_file.csv`}
            rows={validationData.map((row: any, idx: number) => {
              const isValid = checkRowValidity(row);
              return {
                id: idx + 1,
                data: row,
                isValid,
                missingFields: row.missingFields || [],
                status: isValid ? "Valid" : "Invalid",
                errors:
                  row.errors && row.errors.length > 0
                    ? row.errors
                    : isValid
                      ? []
                      : [row.message || "Invalid row"],
              };
            })}
            summary={summary}
            isImporting={isUploading}
            onRemoveFile={(onPrevious) => handleRemoveFile(onPrevious)}
            onImport={(onNext) => handleExecuteImport(onNext)}
            onBack={(onPrevious) => handleRemoveFile(onPrevious)}
          />
        ),
        optional: false,
      },
      {
        title: "Done",
        slug: "done",
        component: (
          <DoneStep
            entityType={selectedEntityLabel as any}
            summary={summary}
            onImportMore={(onGoToStep) => {
              setUploadedFile(null);
              setDataUploadId(null);
              setValidationData([]);
              if (onGoToStep) onGoToStep(0);
            }}
            onClose={onClose}
          />
        ),
        optional: false,
      },
    ],
    [
      selectedEntity,
      selectedEntityLabel,
      uploadedFile,
      validationData,
      summary,
      isUploading,
      onClose,
    ]
  );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-[800px] flex flex-col p-0 max-h-[90vh]">
        <DialogHeader className="px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <FileUp className="w-5 h-5 text-gray-700" />
            Bulk Import
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500 mt-1">
            Import multiple service area entities from a CSV file
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          <Stepper
            steps={steps}
            onStepChange={() => {}}
            onStepSave={async () => {}}
            enableApiSave={false}
            saveOnStepChange={false}
            trackDirtyState={false}
            autoSave={false}
            persistProgress={false}
            allowSkip={false}
            storageKey="bulk-import-modal-v2"
            enableRouting={false}
            onComplete={() => {}}
            showDraftResume={false}
            onValidation={undefined}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
